import { useEffect, useRef, useState } from "react";
import { errorNoti } from "../../../../base/BasicNotifications";
import { readError } from "../_config/quarterlyOkrConfig";
import quarterlyOkrStore, {
  useQuarterlyOkrStore,
} from "../_store/quarterlyOkrStore";
import { shouldPollBrief } from "./briefModel";

// The coach brief for one quarter (handoff 34 §3): GET on open, then every
// ~5 s while it is still being written; stops when it is READY / FAILED or
// the screen is left. Regenerate confirms upstream, then flips to
// IN_PROGRESS and polls.

const POLL_MS = 5000;
const selectBriefs = (state) => state.briefByQc;
const selectPending = (state) => state.pending;

const useCoachBrief = (qcId) => {
  const response = useQuarterlyOkrStore(selectBriefs)[qcId] || null;
  const pending = useQuarterlyOkrStore(selectPending);
  const [error, setError] = useState(null);
  // Hidden after a `regenerate_not_allowed` until the next GET says otherwise.
  const [regenerateBlocked, setRegenerateBlocked] = useState(false);
  const timer = useRef(null);
  const alive = useRef(true);

  const load = () =>
    quarterlyOkrStore
      .getState()
      .fetchBrief(qcId)
      .then((data) => {
        setError(null);
        return data;
      })
      .catch((err) => {
        setError(readError(err).message);
        return null;
      });

  const schedule = (data) => {
    clearTimeout(timer.current);
    if (!alive.current || !shouldPollBrief(data)) return;
    timer.current = setTimeout(
      () => load().then((next) => schedule(next)),
      POLL_MS,
    );
  };

  useEffect(() => {
    alive.current = true;
    setRegenerateBlocked(false);
    load().then((data) => schedule(data));
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
    // eslint-disable-next-line
  }, [qcId]);

  const regenerate = () =>
    quarterlyOkrStore
      .getState()
      .regenerateBrief(qcId)
      .then(() => schedule({ status: "IN_PROGRESS" }))
      .catch((err) => {
        const { code, message } = readError(err);
        if (code === "brief_in_progress") {
          // Already being written — just wait for it.
          schedule({ status: "IN_PROGRESS" });
          return;
        }
        errorNoti(message);
        if (code === "regenerate_not_allowed") setRegenerateBlocked(true);
        load().then((data) => schedule(data));
      });

  return {
    response,
    error,
    regenerating: Boolean(pending[`regenerate:${qcId}`]),
    canRegenerate: Boolean(response?.can_regenerate) && !regenerateBlocked,
    regenerate,
    retry: () => load().then((data) => schedule(data)),
  };
};

export default useCoachBrief;
